import { DateTimeEditor, DropdownEditor, TextEditor } from '@approvalmax/ui';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { MemoSection } from 'pages/requestForm/components';
import { FC, memo, useCallback, useMemo } from 'react';
import { useNetSuitePurchaseOrderFieldSettings } from 'shared/hooks';

import { SectionContainer } from '../../../../../components/SectionContainer';
import { NetSuiteContext, Vendor } from '../../../../../data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import {
    NetSuiteCurrencySection,
    NetSuiteCustomFields,
    NetSuiteIncotermDataProvider,
    NetSuiteVendorsDataProvider,
} from '../../../components';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { useNetSuitePurchaseOrderFields } from '../../NetSuitePurchaseOrderRequestForm.hooks';
import { netSuitePurchaseOrderFieldValuesState } from '../../NetSuitePurchaseOrderRequestForm.states';
import { PurchaseOrderFields } from '../../NetSuitePurchaseOrderRequestForm.types';
import { messages } from './FieldsSection.messages';
import { FieldsWrapper, StyledField } from './FieldsSection.styles';
import { FieldsSectionProps } from './FieldsSection.types';

const { netSuiteConstants } = constants;

export const FieldsSection: FC<FieldsSectionProps> = memo((props) => {
    const {
        workflowId,
        vendorData,
        isVendorDataLoading,
        initialMemoValue,
        canCreateVendor,
        onNewVendorClick,
        updateItemsOnCurrencyChange,
        updateExpenseItemsOnCurrencyChange,
    } = props;

    const fieldValues = useAtomValue(netSuitePurchaseOrderFieldValuesState);
    const showErrors = useAtomValue(netSuiteShowErrorsState);

    const netSuiteEditingContext = useSelector(getNetSuiteContext);

    const { onFieldChange, updateFieldsBulk } = useNetSuitePurchaseOrderFields();

    const { terms, currencies, employees, documentFields } = netSuiteEditingContext;

    const onEditorChange =
        <K extends keyof PurchaseOrderFields>(key: K) =>
        (value: PurchaseOrderFields[K]) =>
            onFieldChange(key, value);

    const onVendorChange = useCallback(
        (vendor: Vendor | null) => {
            updateFieldsBulk({
                vendor: vendor ?? undefined,
                vatRegistrationNumber: vendor?.taxNumber,
                address: null,
                currency: null,
            });
        },
        [updateFieldsBulk]
    );

    const onCurrencyChange = useCallback(
        (currency: NetSuiteContext['currencies'][0]) => {
            updateItemsOnCurrencyChange(currency);
            updateExpenseItemsOnCurrencyChange(fieldValues.currency, currency);

            updateFieldsBulk({
                currency,
                exchangeRate: null,
            });
        },
        [updateFieldsBulk, updateItemsOnCurrencyChange, updateExpenseItemsOnCurrencyChange, fieldValues.currency]
    );

    const vendorCurrencies = useMemo(() => {
        return currencies.filter((currency) => vendorData?.currencyIds?.includes(currency.id));
    }, [vendorData, currencies]);

    const fieldSettings = useNetSuitePurchaseOrderFieldSettings(documentFields);

    const checkCanCreateVendor = useCallback(() => {
        return canCreateVendor;
    }, [canCreateVendor]);

    const createVendorDisplayTextFn = useCallback(() => {
        return messages.newVendor;
    }, []);

    const onChangeMemo = useCallback((value: string) => onFieldChange('memo', value), [onFieldChange]);

    return (
        <>
            <SectionContainer title={messages.vendorDetailsSectionName}>
                <FieldsWrapper>
                    <StyledField title={messages.nsVendorFieldText} required>
                        <NetSuiteVendorsDataProvider>
                            <DropdownEditor
                                value={fieldValues.vendor || null}
                                singleLinePerItem={false}
                                onChange={onVendorChange}
                                placeholder={messages.nsVendorFieldPlaceholder}
                                invalid={showErrors && !fieldValues.vendor}
                                loading={false}
                                canCreateItemFn={checkCanCreateVendor}
                                createItemDisplayTextFn={createVendorDisplayTextFn}
                                onCreateItem={onNewVendorClick}
                            />
                        </NetSuiteVendorsDataProvider>
                    </StyledField>

                    {fieldSettings.billingAddress !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsVendorBillingAddress}
                            required={fieldSettings.billingAddress === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={vendorData?.address ?? []}>
                                <DropdownEditor
                                    value={fieldValues.address || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('address')}
                                    placeholder={messages.nsVendorBillingAddressFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.address &&
                                        fieldSettings.billingAddress === domain.DocumentFieldState.Mandatory
                                    }
                                    loading={isVendorDataLoading}
                                    displayAttribute='fullAddress'
                                    disabled={fieldSettings.billingAddress === domain.DocumentFieldState.ReadOnly}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.vatRegistration !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsVATRegistration}
                            required={fieldSettings.vatRegistration === domain.DocumentFieldState.Mandatory}
                        >
                            <TextEditor
                                disabled={fieldSettings.vatRegistration === domain.DocumentFieldState.ReadOnly}
                                value={fieldValues.vatRegistrationNumber || ''}
                                onChange={onEditorChange('vatRegistrationNumber')}
                                placeholder={messages.textFieldPlaceholder}
                                invalid={
                                    showErrors &&
                                    !fieldValues.vatRegistrationNumber &&
                                    fieldSettings.vatRegistration === domain.DocumentFieldState.Mandatory
                                }
                            />
                        </StyledField>
                    )}
                </FieldsWrapper>
            </SectionContainer>

            <NetSuiteCurrencySection
                currencies={vendorCurrencies}
                fieldValues={fieldValues}
                fieldSettings={fieldSettings}
                isLoadingCurrencies={isVendorDataLoading}
                onExchangeRateChange={onEditorChange('exchangeRate')}
                onCurrencyChange={onCurrencyChange}
            />

            <SectionContainer title={messages.poDetailsSectionName}>
                <FieldsWrapper>
                    {fieldSettings.reference !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsVendorReferenceFieldText}
                            required={fieldSettings.reference === domain.DocumentFieldState.Mandatory}
                        >
                            <TextEditor
                                value={fieldValues.vendorReference || ''}
                                onChange={onEditorChange('vendorReference')}
                                placeholder={messages.textFieldPlaceholder}
                                changeOnBlur
                                maxLength={netSuiteConstants.referenceMaxLength}
                                disabled={fieldSettings.reference === domain.DocumentFieldState.ReadOnly}
                                invalid={
                                    showErrors &&
                                    !fieldValues.vendorReference &&
                                    fieldSettings.reference === domain.DocumentFieldState.Mandatory
                                }
                            />
                        </StyledField>
                    )}

                    {fieldSettings.terms !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsTermsFieldText}
                            required={fieldSettings.terms === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={terms}>
                                <DropdownEditor
                                    value={fieldValues.terms || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('terms')}
                                    placeholder={messages.nsTermsFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.terms &&
                                        fieldSettings.terms === domain.DocumentFieldState.Mandatory
                                    }
                                    loading={false}
                                    disabled={fieldSettings.terms === domain.DocumentFieldState.ReadOnly}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    <StyledField title={messages.nsDateFieldText} required>
                        <DateTimeEditor
                            value={fieldValues.date || null}
                            placeholder={messages.nsDateFieldPlaceholder}
                            invalid={showErrors && !fieldValues.date}
                            onChange={onEditorChange('date')}
                        />
                    </StyledField>

                    {fieldSettings.dueDate !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsDueDateFieldText}
                            required={fieldSettings.dueDate === domain.DocumentFieldState.Mandatory}
                        >
                            <DateTimeEditor
                                value={fieldValues.dueDate || null}
                                placeholder={messages.nsDueDateFieldPlaceholder}
                                onChange={onEditorChange('dueDate')}
                                disabled={fieldSettings.dueDate === domain.DocumentFieldState.ReadOnly}
                                invalid={
                                    showErrors &&
                                    !fieldValues.dueDate &&
                                    fieldSettings.dueDate === domain.DocumentFieldState.Mandatory
                                }
                            />
                        </StyledField>
                    )}

                    {fieldSettings.employee !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsEmployeeFieldText}
                            required={fieldSettings.employee === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={employees}>
                                <DropdownEditor
                                    value={fieldValues.employee || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('employee')}
                                    placeholder={messages.nsEmployeeFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.employee &&
                                        fieldSettings.employee === domain.DocumentFieldState.Mandatory
                                    }
                                    loading={false}
                                    disabled={fieldSettings.dueDate === domain.DocumentFieldState.ReadOnly}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.department !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsDepartmentFieldText}
                            required={fieldSettings.department === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.departments}>
                                <DropdownEditor
                                    value={fieldValues.department || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('department')}
                                    placeholder={messages.nsDepartmentFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.department &&
                                        fieldSettings.department === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={fieldSettings.department === domain.DocumentFieldState.ReadOnly}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.class !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsClassFieldText}
                            required={fieldSettings.class === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.classes}>
                                <DropdownEditor
                                    value={fieldValues.class || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('class')}
                                    placeholder={messages.nsClassFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.class &&
                                        fieldSettings.class === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={fieldSettings.class === domain.DocumentFieldState.ReadOnly}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.location !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsLocationFieldText}
                            required={fieldSettings.location === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.locations}>
                                <DropdownEditor
                                    value={fieldValues.location || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('location')}
                                    placeholder={messages.nsLocationFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.location &&
                                        fieldSettings.location === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={fieldSettings.location === domain.DocumentFieldState.ReadOnly}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.incoterm !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsIncotermFieldText}
                            required={fieldSettings.incoterm === domain.DocumentFieldState.Mandatory}
                        >
                            <NetSuiteIncotermDataProvider>
                                <DropdownEditor
                                    value={fieldValues.incoterm || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('incoterm')}
                                    placeholder={messages.nsIncotermFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.incoterm &&
                                        fieldSettings.incoterm === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={fieldSettings.incoterm === domain.DocumentFieldState.ReadOnly}
                                    loading={false}
                                />
                            </NetSuiteIncotermDataProvider>
                        </StyledField>
                    )}

                    <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} />
                </FieldsWrapper>
            </SectionContainer>

            {fieldSettings.memo !== domain.DocumentFieldState.Hidden && (
                <MemoSection
                    defaultValue={initialMemoValue ?? ''}
                    onChange={onChangeMemo}
                    placeholder={
                        fieldSettings.memo === domain.DocumentFieldState.Editable ? messages.textFieldPlaceholder : ''
                    }
                    maxLength={netSuiteConstants.memoMaxLength}
                    disabled={fieldSettings.memo === domain.DocumentFieldState.ReadOnly}
                    invalid={
                        showErrors && !fieldValues.memo && fieldSettings.memo === domain.DocumentFieldState.Mandatory
                    }
                    width={565}
                />
            )}
        </>
    );
});

FieldsSection.displayName = 'FieldsSection';
